import { router } from "@inertiajs/react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Download,
  Eye,
  Filter,
  MousePointer,
  Plus,
  RotateCcw,
  TrendingUp,
  // TrendingDown, // Commented out with historical comparison
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import CurrencyFormatHint from "@/components/currency-format-hint";
import { HelpButton, HelpDialog } from "@/components/help-dialog";
import LastPurchaseInfo from "@/components/last-purchase-info";
import QuoteItemMatchModal from "@/components/quote-item-match-modal";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHelpDialog } from "@/hooks/use-help-dialog";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { LastPurchaseSummary, PriceComparison } from "@/types";
import { formatDateAR } from "@/utils/date";

interface PriceComparisonTableProps {
  comparison: PriceComparison;
  purchaseRequestId: number;
  lastPurchasesByRequestItemId?: Record<number, LastPurchaseSummary>;
  onOpenHistory?: (
    requestItemId: number,
    requestItemDescription: string,
  ) => void;
}

const formatCurrency = (amount: number, currency = "ARS") => {
  const isUSD = currency === "USD";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: isUSD ? 4 : 2,
    maximumFractionDigits: isUSD ? 4 : 2,
  }).format(amount);
};

const normalizeMetadataValue = (text?: string | null) => {
  if (typeof text !== "string") return "S/E";
  const trimmed = text.trim().toLowerCase();
  if (trimmed.length === 0 || trimmed === "null") return "S/E";
  return text.trim();
};

// Structured Price Cell - renders price info in consistent rows
function PriceCell({
  price,
  isBestPrice,
  // lastPurchase temporarily unused while historical comparison is commented out
  lastPurchase: _lastPurchase,
}: {
  price: {
    unit_price_ars: number | null;
    unit_price_original?: number;
    currency: string;
    percentage_diff_from_best?: number | null;
  };
  isBestPrice: boolean;
  lastPurchase?: LastPurchaseSummary;
}) {
  if (price.unit_price_ars === null) {
    return <span className="text-muted-foreground">—</span>;
  }

  const arsFormatted = formatCurrency(price.unit_price_ars, "ARS");
  const isUsd = price.currency === "USD" && price.unit_price_original;
  const usdFormatted = isUsd
    ? formatCurrency(price.unit_price_original!, "USD")
    : null;

  // Calculate historical comparison (commented out pending user feedback)
  // let historyDiff: number | null = null;
  // let historyIsHigher = false;
  // let historyIsLower = false;
  // if (isBestPrice && lastPurchase?.unit_price_ars && price.unit_price_ars) {
  //   historyDiff =
  //     ((price.unit_price_ars - lastPurchase.unit_price_ars) /
  //       lastPurchase.unit_price_ars) *
  //     100;
  //   historyIsHigher = historyDiff > 0;
  //   historyIsLower = historyDiff < 0;
  // }

  return (
    <div className="text-center divide-y divide-border/50">
      {/* Row 1: Price in ARS - always visible */}
      <div
        className={`font-semibold tabular-nums py-0.5 ${isBestPrice ? "text-green-700" : ""}`}
      >
        {arsFormatted}
      </div>

      {/* Row 2: Best price indicator OR % difference */}
      {isBestPrice ? (
        <div className="text-xs text-green-600 font-medium flex items-center justify-center gap-1 py-0.5">
          <Check className="size-3" />
          <span>Mejor precio</span>
        </div>
      ) : price.percentage_diff_from_best != null &&
        Number(price.percentage_diff_from_best) > 0 ? (
        <div className="text-xs text-orange-600 tabular-nums py-0.5">
          +{Number(price.percentage_diff_from_best).toFixed(1)}%
        </div>
      ) : (
        <div className="text-xs text-transparent select-none py-0.5">—</div>
      )}

      {/* Row 3: Historical comparison - COMMENTED OUT pending user feedback
      {isBestPrice && historyDiff !== null && Math.abs(historyDiff) > 0.05 ? (
        <div
          className={`text-xs tabular-nums flex items-center justify-center gap-0.5 py-0.5 ${
            historyIsHigher
              ? "text-red-600"
              : historyIsLower
                ? "text-green-600"
                : "text-muted-foreground"
          }`}
        >
          {historyIsHigher ? (
            <TrendingUp className="size-3" />
          ) : historyIsLower ? (
            <TrendingDown className="size-3" />
          ) : null}
          <span>vs {formatCurrency(lastPurchase!.unit_price_ars!)}</span>
          <span className="font-medium ml-0.5">
            {historyIsHigher ? "+" : ""}
            {historyDiff.toFixed(1)}%
          </span>
        </div>
      ) : (
        <div className="text-xs text-transparent select-none py-0.5">—</div>
      )}
      */}

      {/* Row 3: Price in USD - only if currency is USD */}
      {isUsd ? (
        <div className="text-xs text-blue-600 tabular-nums py-0.5">
          {usdFormatted}
        </div>
      ) : (
        <div className="text-xs text-transparent select-none py-0.5">—</div>
      )}
    </div>
  );
}

function USDConfigDialog({
  comparison,
  isOpen,
  setIsOpen,
}: {
  comparison: PriceComparison;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
}) {
  const routes = useOrganizationRoutes();
  const [internalOpen, setInternalOpen] = useState(false);
  const modalOpen = isOpen ?? internalOpen;
  const setModalOpen = setIsOpen ?? setInternalOpen;
  const [exchangeRates, setExchangeRates] = useState<Record<number, string>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const usdSuppliers = comparison.headers.suppliers.filter(
    (s) => s.currency === "USD",
  );

  if (usdSuppliers.length === 0) return null;

  const handleExchangeRateChange = (quoteId: number, value: string) => {
    setExchangeRates((prev) => ({ ...prev, [quoteId]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      for (const [quoteId, rate] of Object.entries(exchangeRates)) {
        const numericRate = parseFloat(rate);
        if (numericRate > 0) {
          await new Promise<void>((resolve) => {
            router.patch(
              routes.quotes.show(Number(quoteId)),
              { exchange_rate: numericRate },
              {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => resolve(),
                onError: () => resolve(),
              },
            );
          });
        }
      }
      setModalOpen(false);
      setExchangeRates({});
      router.reload({ only: ["price_comparison"] });
    } catch (error) {
      console.error("Error updating exchange rates:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const needsConfiguration = usdSuppliers.some(
    (s) => !s.exchange_rate || s.exchange_rate <= 0,
  );

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <DollarSign className="size-4" />
          <span className="hidden sm:inline">USD</span>
          {needsConfiguration && (
            <AlertTriangle className="size-3.5 text-amber-500" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurar Tipos de Cambio USD</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {usdSuppliers.map((supplier) => (
            <div
              key={supplier.id}
              className="flex items-center gap-3 p-3 border rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium text-sm">{supplier.name}</div>
                <div className="text-xs text-muted-foreground">
                  Quote #{supplier.quote_id}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder={
                    supplier.exchange_rate
                      ? supplier.exchange_rate.toString()
                      : "1200.00"
                  }
                  value={
                    exchangeRates[supplier.quote_id] ??
                    supplier.exchange_rate ??
                    ""
                  }
                  onChange={(e) =>
                    handleExchangeRateChange(supplier.quote_id, e.target.value)
                  }
                  className="w-24 text-right"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          ))}
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            className="w-full"
            disabled={isSubmitting || Object.keys(exchangeRates).length === 0}
          >
            {isSubmitting ? "Aplicando..." : "Aplicar Cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SupplierFilterPopover({
  suppliers,
  visibleSupplierIds,
  onToggle,
  onShowAll,
}: {
  suppliers: { id: number; name: string }[];
  visibleSupplierIds: Set<number>;
  onToggle: (id: number) => void;
  onShowAll: () => void;
}) {
  const allVisible = visibleSupplierIds.size === suppliers.length;
  const hiddenCount = suppliers.length - visibleSupplierIds.size;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="size-4" />
          <span className="hidden sm:inline">Proveedores</span>
          {hiddenCount > 0 && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({hiddenCount} ocultos)
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Mostrar Proveedores</span>
            {!allVisible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowAll}
                className="h-auto p-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <RotateCcw className="size-3 mr-1" />
                Ver todos
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {suppliers.map((supplier) => {
              const isVisible = visibleSupplierIds.has(supplier.id);
              const canHide = visibleSupplierIds.size > 1;
              return (
                <div key={supplier.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`supplier-${supplier.id}`}
                    checked={isVisible}
                    disabled={isVisible && !canHide}
                    onCheckedChange={() => onToggle(supplier.id)}
                  />
                  <Label
                    htmlFor={`supplier-${supplier.id}`}
                    className="text-sm cursor-pointer flex-1 truncate"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="truncate block max-w-[180px]">
                          {supplier.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="start">
                        {supplier.name}
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  {isVisible && (
                    <Eye className="size-3 text-muted-foreground" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function PriceComparisonTable({
  comparison,
  purchaseRequestId,
  lastPurchasesByRequestItemId = {},
  onOpenHistory,
}: PriceComparisonTableProps) {
  const routes = useOrganizationRoutes();
  const [usdConfigOpen, setUsdConfigOpen] = useState(false);
  const [hiddenSupplierIds, setHiddenSupplierIds] = useState<Set<number>>(
    new Set(),
  );
  const visibleSupplierIds = new Set(
    comparison.headers.suppliers
      .map((s) => s.id)
      .filter((id) => !hiddenSupplierIds.has(id)),
  );
  const help = useHelpDialog("priceComparison");

  // Scroll navigation state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  const scrollByDirection = useCallback((direction: "left" | "right") => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const columnWidth = 150; // supplier column width
    el.scrollBy({
      left: direction === "left" ? -columnWidth * 2 : columnWidth * 2,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  if (!comparison?.rows.length) {
    return null;
  }

  const {
    headers,
    rows,
    available_request_items = [],
    all_quote_items_by_supplier = {},
  } = comparison;

  const filteredSuppliers = headers.suppliers.filter((s) =>
    visibleSupplierIds.has(s.id),
  );
  const allSuppliersVisible =
    visibleSupplierIds.size === headers.suppliers.length;

  const toggleSupplier = (supplierId: number) => {
    setHiddenSupplierIds((prev) => {
      const next = new Set(prev);
      if (next.has(supplierId)) {
        next.delete(supplierId);
      } else {
        next.add(supplierId);
      }
      return next;
    });
  };

  const showAllSuppliers = () => {
    setHiddenSupplierIds(new Set());
  };

  const requestItemMap = new Map<number, { product_id?: number | null }>(
    available_request_items.map((ri) => [
      ri.id,
      { product_id: ri.product_id ?? null },
    ]),
  );

  const handleMatchUpdate = () => {
    router.reload();
  };

  const openUsdConfig = () => {
    setUsdConfigOpen(true);
  };

  const hasUsdSuppliers = headers.suppliers.some((s) => s.currency === "USD");

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-xl">Comparativa de Precios</CardTitle>
            <CardDescription>
              {headers.suppliers.length} proveedores · {rows.length} artículos
            </CardDescription>
          </div>
          <ButtonGroup>
            {headers.suppliers.length > 1 && (
              <SupplierFilterPopover
                suppliers={headers.suppliers}
                visibleSupplierIds={visibleSupplierIds}
                onToggle={toggleSupplier}
                onShowAll={showAllSuppliers}
              />
            )}
            {hasUsdSuppliers && (
              <USDConfigDialog
                comparison={comparison}
                isOpen={usdConfigOpen}
                setIsOpen={setUsdConfigOpen}
              />
            )}
            <Button variant="outline" size="sm" asChild>
              <a
                href={routes.purchaseRequests.exportPriceComparison(
                  purchaseRequestId,
                )}
                download
                className="inline-flex items-center gap-2"
              >
                <Download className="size-4" />
                <span className="hidden sm:inline">Exportar</span>
              </a>
            </Button>
            <HelpButton onClick={help.open} label="Ayuda" variant="outline" />
          </ButtonGroup>
          <HelpDialog
            content={help.content}
            open={help.isOpen}
            onOpenChange={help.setIsOpen}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <CurrencyFormatHint />

        {/* Warnings */}
        {comparison.warnings && comparison.warnings.length > 0 && (
          <div className="my-4 space-y-2">
            {comparison.warnings.map((warning, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-4 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">{warning.message}</p>
                </div>
                {warning.quote_id && (
                  <Button size="sm" variant="outline" onClick={openUsdConfig}>
                    Configurar
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Hidden suppliers indicator */}
        {!allSuppliersVisible && (
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="size-4" />
            <span>
              Mostrando {visibleSupplierIds.size} de {headers.suppliers.length}{" "}
              proveedores
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={showAllSuppliers}
              className="h-auto p-0 text-sm"
            >
              Ver todos
            </Button>
          </div>
        )}

        <div className="relative">
          {/* Left navigation button */}
          {canScrollLeft && (
            <button
              onClick={() => scrollByDirection("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white shadow-lg border-0 rounded-full p-2.5 transition-all hover:scale-110"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="max-h-[85vh] overflow-y-scroll overflow-x-scroll rounded-lg border [&_[data-slot=table-container]]:!overflow-visible"
          >
            <Table>
              <TableHeader className="sticky top-0 z-20 bg-muted after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[2px] after:bg-slate-900 dark:after:bg-slate-100">
                <TableRow className="border-b">
                  <TableHead className="min-w-[200px] max-w-[280px] sticky left-0 top-0 z-30 bg-muted font-semibold after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100">
                    Artículo
                  </TableHead>
                  <TableHead className="text-center w-20 px-3 font-semibold border-r">
                    Cant.
                  </TableHead>
                  <TableHead className="text-center w-16 px-3 font-semibold border-r">
                    U.M
                  </TableHead>
                  {filteredSuppliers.map((supplier) => (
                    <TableHead
                      key={supplier.quote_id}
                      className="text-center min-w-[150px] px-4 font-semibold border-r last:border-r-0"
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="truncate cursor-default">
                            {supplier.name}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {supplier.name}
                        </TooltipContent>
                      </Tooltip>
                      <div className="text-xs font-normal text-muted-foreground mt-1">
                        {supplier.currency === "USD" && supplier.exchange_rate
                          ? `USD @ ${supplier.exchange_rate}`
                          : supplier.quote_date
                            ? formatDateAR(supplier.quote_date)
                            : ""}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, rowIndex) => {
                  // Solid backgrounds for alternating rows (prevents content showing through sticky column)
                  const isOddRow = rowIndex % 2 === 1;
                  const rowBg = isOddRow
                    ? "bg-slate-100 dark:bg-slate-800"
                    : "bg-background";
                  const stickyBg = isOddRow
                    ? "bg-slate-100 dark:bg-slate-800"
                    : "bg-background";
                  return (
                    <TableRow key={row.request_item.id} className={rowBg}>
                      <TableCell
                        className={`max-w-[280px] sticky left-0 ${stickyBg} z-10 after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100`}
                      >
                        <div className="space-y-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="max-w-[220px] truncate font-medium cursor-default">
                                {row.request_item.description}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent
                              side="right"
                              className="max-w-[350px] whitespace-normal"
                            >
                              {row.request_item.description}
                            </TooltipContent>
                          </Tooltip>
                          <LastPurchaseInfo
                            requestItemId={row.request_item.id}
                            requestItemDescription={
                              row.request_item.description
                            }
                            hasProductAssigned={Boolean(
                              requestItemMap.get(row.request_item.id)
                                ?.product_id,
                            )}
                            lastPurchase={
                              lastPurchasesByRequestItemId[row.request_item.id]
                            }
                            onOpenHistory={onOpenHistory}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-center tabular-nums border-r">
                        {row.request_item.quantity.toLocaleString("es-AR")}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-sm border-r">
                        {row.request_item.unit}
                      </TableCell>
                      {filteredSuppliers.map((supplier) => {
                        const originalIndex = headers.suppliers.findIndex(
                          (s) => s.id === supplier.id,
                        );
                        const price = row.prices[originalIndex];
                        const isBestPrice = price?.is_best_price ?? false;

                        return (
                          <TableCell
                            key={supplier.quote_id}
                            className="text-center px-3 py-2 border-r last:border-r-0"
                          >
                            {price ? (
                              price.missing_exchange_rate ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="inline-flex items-center gap-1 text-amber-600 cursor-help">
                                      <AlertTriangle className="size-3.5" />
                                      <span className="text-sm tabular-nums">
                                        {formatCurrency(
                                          price.unit_price_original ?? 0,
                                          price.currency,
                                        )}
                                      </span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-sm">
                                      Sin tipo de cambio
                                    </p>
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      className="mt-2 w-full"
                                      onClick={openUsdConfig}
                                    >
                                      Configurar
                                    </Button>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <QuoteItemMatchModal
                                  requestItemId={row.request_item.id}
                                  requestItemDescription={
                                    row.request_item.description
                                  }
                                  supplierName={supplier.name}
                                  allSupplierQuoteItems={
                                    all_quote_items_by_supplier[supplier.id] ||
                                    []
                                  }
                                  currentQuoteItemId={price.quote_item_id}
                                  onMatchUpdate={handleMatchUpdate}
                                >
                                  <div className="cursor-pointer hover:bg-muted/60 p-2 -m-2 rounded transition-colors relative group">
                                    <PriceCell
                                      price={price}
                                      isBestPrice={isBestPrice}
                                      lastPurchase={
                                        lastPurchasesByRequestItemId[
                                          row.request_item.id
                                        ]
                                      }
                                    />
                                    {available_request_items.length > 1 && (
                                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <div className="bg-background/90 shadow-sm border rounded px-2 py-1 text-xs text-muted-foreground flex items-center gap-1">
                                          <MousePointer className="size-3" />
                                          Re-asignar
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </QuoteItemMatchModal>
                              )
                            ) : all_quote_items_by_supplier[supplier.id]
                                ?.length > 0 ? (
                              <QuoteItemMatchModal
                                requestItemId={row.request_item.id}
                                requestItemDescription={
                                  row.request_item.description
                                }
                                supplierName={supplier.name}
                                allSupplierQuoteItems={
                                  all_quote_items_by_supplier[supplier.id] || []
                                }
                                currentQuoteItemId={undefined}
                                onMatchUpdate={handleMatchUpdate}
                              >
                                <div className="cursor-pointer hover:bg-blue-50 border-2 border-dashed border-muted-foreground/30 hover:border-blue-400 rounded-md p-2 transition-colors">
                                  <div className="flex items-center justify-center gap-1 text-muted-foreground hover:text-blue-600 transition-colors">
                                    <Plus className="size-4" />
                                    <span className="text-sm">Asignar</span>
                                  </div>
                                </div>
                              </QuoteItemMatchModal>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}

                {/* Totals Row */}
                {comparison.totals && (
                  <TableRow className="border-t-2 font-semibold bg-slate-200 dark:bg-slate-700">
                    <TableCell
                      className="sticky left-0 bg-slate-200 dark:bg-slate-700 z-10 after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100"
                      colSpan={3}
                    >
                      <span className="text-base font-bold">TOTAL</span>
                    </TableCell>
                    {filteredSuppliers.map((supplier) => {
                      const supplierTotal =
                        comparison.totals!.supplier_totals.find(
                          (st) => st.supplier_id === supplier.id,
                        );
                      const isBestTotal =
                        comparison.totals!.best_total_supplier_id ===
                        supplier.id;

                      return (
                        <TableCell
                          key={supplier.quote_id}
                          className="text-center px-3 border-r last:border-r-0"
                        >
                          {supplierTotal ? (
                            <div className="space-y-0.5">
                              <div
                                className={`text-base tabular-nums font-bold ${isBestTotal ? "text-green-700" : ""}`}
                              >
                                {formatCurrency(supplierTotal.total)}
                              </div>
                              {isBestTotal && (
                                <div className="text-xs text-green-600 font-medium flex items-center justify-center gap-1">
                                  <Check className="size-3" />
                                  Mejor total
                                </div>
                              )}
                              {supplierTotal.missing_items > 0 && (
                                <div className="text-xs text-orange-600">
                                  ({supplierTotal.missing_items} sin precio)
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Commercial Terms Rows */}
                <TableRow className="bg-slate-50 dark:bg-slate-900 border-t">
                  <TableCell
                    className="font-medium sticky left-0 bg-slate-50 dark:bg-slate-900 z-10 text-muted-foreground text-sm after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100"
                    colSpan={3}
                  >
                    Forma de pago
                  </TableCell>
                  {filteredSuppliers.map((supplier) => (
                    <TableCell
                      key={supplier.quote_id}
                      className="text-center text-sm border-r last:border-r-0"
                    >
                      {normalizeMetadataValue(supplier.payment_method)}
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow className="bg-slate-50 dark:bg-slate-900">
                  <TableCell
                    className="font-medium sticky left-0 bg-slate-50 dark:bg-slate-900 z-10 text-muted-foreground text-sm after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100"
                    colSpan={3}
                  >
                    Plazo de pago
                  </TableCell>
                  {filteredSuppliers.map((supplier) => {
                    const days = supplier.payment_term_days;
                    const isValid =
                      days != null && days !== "null" && days !== "";
                    return (
                      <TableCell
                        key={supplier.quote_id}
                        className="text-center text-sm border-r last:border-r-0"
                      >
                        {isValid ? `${days} días` : "S/E"}
                      </TableCell>
                    );
                  })}
                </TableRow>

                <TableRow className="bg-slate-50 dark:bg-slate-900">
                  <TableCell
                    className="font-medium sticky left-0 bg-slate-50 dark:bg-slate-900 z-10 text-muted-foreground text-sm after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100"
                    colSpan={3}
                  >
                    Plazo de entrega
                  </TableCell>
                  {filteredSuppliers.map((supplier) => (
                    <TableCell
                      key={supplier.quote_id}
                      className="text-center text-sm border-r last:border-r-0"
                    >
                      {normalizeMetadataValue(supplier.delivery_time)}
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow className="bg-slate-50 dark:bg-slate-900">
                  <TableCell
                    className="font-medium sticky left-0 bg-slate-50 dark:bg-slate-900 z-10 text-muted-foreground text-sm after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100"
                    colSpan={3}
                  >
                    Validez oferta
                  </TableCell>
                  {filteredSuppliers.map((supplier) => (
                    <Tooltip key={supplier.quote_id}>
                      <TooltipTrigger asChild>
                        <TableCell className="text-center text-sm truncate max-w-[120px] cursor-default border-r last:border-r-0">
                          {normalizeMetadataValue(supplier.validity_text)}
                        </TableCell>
                      </TooltipTrigger>
                      <TooltipContent>
                        {normalizeMetadataValue(supplier.validity_text)}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Right navigation button */}
          {canScrollRight && (
            <button
              onClick={() => scrollByDirection("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white shadow-lg border-0 rounded-full p-2.5 transition-all hover:scale-110"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Savings Analysis */}
        {comparison.totals?.best_prices_sum != null &&
          comparison.totals.best_prices_sum > 0 && (
            <div className="mt-6 p-4 bg-muted/30 border rounded-lg space-y-2">
              <div className="flex items-center gap-2 font-semibold">
                <TrendingUp className="size-5 text-primary" />
                Análisis de Ahorro
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">
                  Suma mejores precios:
                </span>
                <span className="font-bold tabular-nums">
                  {formatCurrency(comparison.totals.best_prices_sum)}
                </span>
              </div>
              {comparison.totals.combined_savings && (
                <>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                      Mejor proveedor completo (
                      {
                        comparison.totals.combined_savings
                          .best_complete_supplier_name
                      }
                      ):
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(
                        comparison.totals.combined_savings.best_complete_total,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t text-sm">
                    <span className="font-medium text-green-700">
                      Ahorro potencial:
                    </span>
                    <span className="font-bold text-green-700 tabular-nums">
                      {formatCurrency(
                        comparison.totals.combined_savings.amount,
                      )}{" "}
                      ({comparison.totals.combined_savings.percentage}%)
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

        {headers.suppliers.some((s) => s.currency === "USD") && (
          <p className="mt-4 text-xs text-muted-foreground">
            * Precios USD convertidos a ARS según tipo de cambio configurado.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
